import React, { useCallback } from 'react';
import styled from 'styled-components';

import useDebounce from '@front/common/src/hooks/use-debounce';
import { toRem } from '@front/common/src/utils/styles';
import colors, { withOpacity } from '@front/common/src/utils/colors';

import { translate } from '@front/i18n';

import IconButton from '@front/web-ui/src/components/button/icon-button';
import { minWidth } from '@front/web-ui/src/styles/mixins';

import { route } from '../../routes/routes';
import { useStepper } from '../../routes/use-stepper';
import { useNavigateHistoryWithQuery } from '../../hooks/use-navigate-history-with-query';
import { useShowHeader } from '../../hooks/use-show-header';

const t = translate('components.formProgressBar');

const Container = styled.div`
  display: flex;
  align-items: center;
  padding: 20px 20px 0;
  ${minWidth.md`
    padding: 30px 30px 0 30px;
  `};
`;

const Bar = styled.div`
  height: ${toRem(6)};
  border-radius: ${toRem(3)};
  background-color: ${withOpacity(colors.ink50, 0.2)};
`;

const Fill = styled.div<{ $width: number }>`
  height: ${toRem(6)};
  border-radius: ${toRem(3)};
  background-color: ${colors.ink100};
  width: ${({ $width }) => $width}%;
  transition: width 500ms ease-out;
`;

const StyledProgressBar = styled(Bar)`
  width: 200px;
  margin: auto;
`;
export const FormProgressBar = () => {
  const { historyPushWithQuery } = useNavigateHistoryWithQuery();
  const { previousRoute, current, total, stepConfig } = useStepper();
  const stepNumber = current + 1;
  const ratio = stepNumber / total;
  useShowHeader({ shouldHide: current !== 0 || stepNumber === total });

  // We use debounced value here to animate the first step progress.
  // Without debounce the progress bar width start at 20% and not 0% which leads to no animation
  const debouncedRatio = useDebounce(ratio, 150);

  const handlePrevious = useCallback(
    () => historyPushWithQuery(route(previousRoute)),
    [historyPushWithQuery, previousRoute],
  );
  // "!stepConfig" condition is necessary because routes that are not in the use-stepper can't have their
  // associated key in the stepConfig and we don't want to show the progress bar for those routes.
  if (!stepConfig || (stepConfig && !stepConfig.shouldShowProgressBar)) return null;

  return (
    <Container>
      <IconButton label={t('base.back')} type='white' iconName='chevron-left' onPress={handlePrevious} />
      <StyledProgressBar role='progressbar' aria-label={t('.progressionLabel', { ratio: Math.round(ratio * 100) })}>
        <Fill $width={debouncedRatio * 100} />
      </StyledProgressBar>
    </Container>
  );
};
